/**
 * Fetch Place Details from Google Places API (by place_id).
 * Used to enhance venues with phone, website, and location from Google Maps.
 * Requires Google Maps script with Places library to be loaded (e.g. via PlaceAutocomplete).
 */

/**
 * Get detailed place data by place_id using PlacesService.getDetails.
 * @param {string} placeId - Google Place ID
 * @returns {Promise<{ formattedAddress: string|null, latitude: number|null, longitude: number|null, placeId: string|null, formattedPhoneNumber: string|null, website: string|null, name: string|null }>}
 */
export function getPlaceDetails(placeId) {
  if (!placeId || typeof placeId !== 'string' || !placeId.trim()) {
    return Promise.resolve(null);
  }
  if (typeof window === 'undefined' || !window.google?.maps?.places) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    try {
      const div = document.createElement('div');
      const service = new window.google.maps.places.PlacesService(div);
      const request = {
        placeId: placeId.trim(),
        fields: ['formatted_address', 'geometry', 'place_id', 'formatted_phone_number', 'website', 'name'],
      };
      service.getDetails(request, (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null);
          return;
        }
        const formattedAddress = (place.formatted_address || '').trim() || null;
        let latitude = null;
        let longitude = null;
        if (place.geometry?.location) {
          latitude = place.geometry.location.lat();
          longitude = place.geometry.location.lng();
        }
        const pid = (place.place_id || '').trim() || null;
        const formattedPhoneNumber = (place.formatted_phone_number || '').trim() || null;
        const website = (place.website || '').trim() || null;
        const name = (place.name || '').trim() || null;
        resolve({
          formattedAddress,
          latitude,
          longitude,
          placeId: pid,
          formattedPhoneNumber,
          website,
          name,
        });
      });
    } catch (err) {
      console.warn('Place Details fetch failed:', err);
      resolve(null);
    }
  });
}

/**
 * Build Google Maps URL from place_id or lat,lng.
 */
export function buildMapUrlFromPlaceDetails(details) {
  if (!details) return null;
  const pid = (details.placeId || '').trim();
  if (pid) {
    return `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${encodeURIComponent(pid)}`;
  }
  const lat = details.latitude;
  const lng = details.longitude;
  if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }
  return null;
}
